import axios from 'axios';
import { IS_TESTNET, TONCENTER_API_KEY, TONCENTER_V3_API_ENDPOINT } from '~/config.js';
// import { hexToBase64 } from '~/utils.js';
import { canonizeAddress, isSameAddress } from '~/tonweb';
import { parseAddressCell, parseDedustAsset } from '~/boc.js';
import { bytesToHex, parseBocString } from '~/cell.js';
import { getJettonPricesDyor } from '~/api/dyor';
import { getLpPoolTypeByDex, isNativeTonAddress, TON_NATIVE_ASSET } from '~/lpPools.js';
import { dedupeInFlight } from '~/api/dedupe.js';
// The native coin has no metadata of its own, so swaps draw its logo from here:
import GRAM_LOGO_URL from '@img/icons/tonscan/gram-logo.svg';
import store from '../store.js';

/* eslint camelcase: "off", func-names: "off" */

// Disable headers if api key is not set. Otherwise
// axios will make a pre-flight request:
const httpHeaders = TONCENTER_API_KEY
    ? { 'X-API-Key': TONCENTER_API_KEY }
    : undefined;

const http = axios.create({
    baseURL: TONCENTER_V3_API_ENDPOINT,
    headers: httpHeaders,
});

const FRAGMENT_TREASURY = 'UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB';
const FRAGMENT_TREASURY_RAW = '0:408DA3B28B6C065A593E10391269BAAA9C5F8CAEBC0C69D9F0AABBAB2A99256B';

// New actions from Toncenter
const supported_action_types = [
    'multisig_create_order',
    'multisig_approve',
    'multisig_execute',
    'jvault_stake',
    'jvault_unstake',
    'jvault_claim',
    'jvault_unstake_request',
    'vesting_send_message',
    'vesting_add_whitelist',
    'evaa_supply',
    'evaa_withdraw',
    'evaa_liquidate',
    'teleitem_start_auction',
    'teleitem_cancel_auction',
    'gasless_request',
    'change_wallet_key',
];

// Every Telegram wallet keeps only a trampoline as its code; the wallet
// itself lives in config param -123, so all of them share this hash:
export const TG_WALLET_CODE_HASH = 'kUmuUcHkaJcQzr94MCl7Fqz7rbNjqSClN4k+f/7sp2g=';

const isTgWallet = (addressBook, address) => Array.isArray(addressBook?.[address]?.interfaces)
    && addressBook[address].interfaces.includes('tg_wallet');

const sameTransactions = (a = [], b = []) => a.length === b.length && a.every((hash, i) => hash === b[i]);

/**
 * A relayed (gasless) request is reported twice on the same message: as the
 * gasless_request marker and as a plain call_contract with the same source,
 * destination and value. One row is enough, and the marker is the one that
 * says who paid.
 *
 * @param  {Array} actions  raw toncenter actions
 * @return {Array}
 */
export const dropGaslessTwins = function (actions = []) {
    const markers = actions.filter(action => action?.type === 'gasless_request');

    if (markers.length === 0) {
        return actions;
    }

    return actions.filter(action => action?.type !== 'call_contract'
        || !markers.some(marker => marker.details?.source === action.details?.source
            && marker.details?.destination === action.details?.destination
            && sameTransactions(marker.transactions, action.transactions)));
};

/**
 * In an account's history a Telegram wallet's gasless transfer is one row, not
 * the relayer's marker plus the transfer. The transfer keeps the link
 * (gasless_parent), so a marker with its child in the batch goes; a lone one
 * stays. Markers of other relayed wallets stay too: their transfers carry no mark.
 *
 * @param  {Array} actions  raw toncenter actions
 * @param  {Object} addressBook
 * @return {Array}
 */
export const dropGaslessMarkers = function (actions = [], addressBook = {}) {
    const parents = new Set(actions.map(action => action?.parent_gasless_action).filter(Boolean));

    return actions.filter(action => action?.type !== 'gasless_request'
        || !parents.has(action.action_id)
        || !isTgWallet(addressBook, action.details?.destination));
};

/**
 * /pendingActions and the stream take no exclude_action_type, so the action
 * groups switched off in the history filter are dropped here. It goes first,
 * on raw toncenter types, the way /actions leaves them out on its side.
 *
 * @param  {Array} actions  raw toncenter actions
 * @return {Array}
 */
export const dropExcludedActions = function (actions = []) {
    const excluded = store.state.excludeGroups.flatMap(group => store.state.actionGroups[group] || []);

    return actions.filter(action => !excluded.includes(action?.type));
};

/**
 * This function returns last blocks sorted by time.
 *
 * @param  {Numer} options.wc
 * @param  {Number} options.limit
 * @param  {Number} options.offset
 * @param  {Number} options.startUtime
 * @param  {Number} options.endUtime
 * @return {Promise<Array>}
 */
export const getPreviousBlocks = async function ({ wc, limit, offset, startUtime, endUtime } = {}) {
    const { data: { blocks: result } } = await http.get('/blocks', {
        params: {
            offset, limit,
            workchain: wc,
            start_utime: startUtime,
            end_utime: endUtime,
            sort: 'desc',
        },
    });

    // root_hash уже приходит в base64 из API

    return result.map(Object.freeze);
};

/**
 * Transactions per second over the last minute of basechain blocks. The
 * newest second may be indexed only in part, so it is left out.
 *
 * @return {Promise<Number|undefined>} undefined when the blocks cover too little time
 */
export const getBasechainTps = async function () {
    const blocks = await getPreviousBlocks({ wc: 0, limit: 1000, startUtime: Math.floor(Date.now() / 1000) - 75 });
    if (!blocks.length) {
        return undefined;
    }

    const times = blocks.map(block => Number(block.gen_utime));
    const end = Math.max(...times) - 1;
    const start = Math.max(end - 60, Math.min(...times));

    if (end - start < 30) {
        return undefined;
    }

    const count = blocks
        .filter(block => Number(block.gen_utime) > start && Number(block.gen_utime) <= end)
        .reduce((sum, block) => sum + Number(block.tx_count), 0);

    return count / (end - start);
};

export const getBlockHeader = async function ({ workchain, shard, seqno }) {
    const query = { workchain, shard, seqno, limit: 1 };

    const { data: { blocks } } = await http.get('/blocks', { params: query });

    return blocks[0];
};

const getSourceAndDestination = function (msg, address, hash, addressBook) {
    const from = msg.source ? addressBook[msg.source].user_friendly : null;
    const to = msg.destination ? addressBook[msg.destination].user_friendly : null;

    return {
        from, to,
        is_out: address === from,
        amount: msg.value || null,
        created_at: msg.created_at,
        hash,
    };
};

/**
 * @param  {Number} options.wc
 * @param  {Number} options.startUtime
 * @param  {Number} options.endUtime
 * @return {Promise<Array>}
 */
export const getAllTransactions = async function ({ wc, limit, startUtime, endUtime } = {}) {
    const { data } = await http.get('/transactions', {
        params: {
            limit,
            workchain: wc,
            start_utime: startUtime,
            end_utime: endUtime,
            sort: 'desc',
        },
    });

    const result = data.transactions;
    const addressBook = data.address_book;

    const transactions = result.map((tx) => {
        const address = tx.account;
        const hash = tx.hash;

        const is_service = !tx.in_msg && tx.out_msgs.length === 0;
        const is_external = tx.out_msgs.length === 0 && !tx.in_msg?.source && tx.in_msg?.destination === address;
        let msg = undefined;

        if (is_service) {
            msg = {
                source: address,
                destination: null,
                created_at: tx.created_at || tx.now,
            };
        } else if (tx.out_msgs.length > 0) {
            msg = tx.out_msgs.at(-1);
        } else {
            msg = tx.in_msg;
        }

        const sourceAndDestination = getSourceAndDestination(msg, address, hash, addressBook);

        sourceAndDestination.is_service = is_service;
        sourceAndDestination.is_external = is_external;
        sourceAndDestination.created_at = tx.now;

        return sourceAndDestination;
    });

    return transactions.sort((a, b) => b.created_at - a.created_at).map(Object.freeze);
};

/**
 * @see https://api.toncenter.com/index/#/default/get_top_accounts_by_balance_v1_topAccountsByBalance_get
 * @param  {Number} options.limit
 * @param  {Number} options.offset
 * @return {Promise<Array>}
 */
export const getTopBalances = async function ({ limit, offset } = {}) {
    const { data } = await http.get('/topAccountsByBalance', {
        params: { limit, offset },
    });

    return data.map(item => ({
        ...item,
        account: canonizeAddress(item.account),
    }));
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByHash = async function (hash) {
    const { data } = await http.get('/transactions', { params: { hash } });

    return Object.freeze(data);
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByInMsgHash = async function (hash) {
    const { data } = await http.get('/transactionsByMessage', {
        params: {
            msg_hash: hash,
            direction: 'in',
        },
    });

    return Object.freeze(data);
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByHashOrInMessageHash = async function (hash) {
    const byHash = await getTransactionByHash(hash);

    if (byHash?.transactions?.length > 0) {
        return byHash;
    }

    /* eslint no-return-await: "off" */
    return await getTransactionByInMsgHash(hash);
};

/**
 * Domain points to whatever is written in its wallet record,
 * the owner is only a fallback for domains without such record.
 *
 * @param  {String} domain
 * @return {Promise<String|undefined>}
 */
export const getAddressByDomain = async function (domain) {
    const { data } = await http.get('/dns/records', { params: { domain } });

    const dns = data.records?.[0];

    if (!dns) {
        return undefined;
    }

    const address = dns.dns_wallet || dns.nft_item_owner;

    return data.address_book?.[address]?.user_friendly;
};

/**
 * @param  {Number} options.workchain
 * @param  {Number} options.shard
 * @param  {Number} options.seqno
 * @return {Promise<Object>}
 */
export const getBlockTransactions = async function ({ workchain, shard, seqno, offset, limit = 40 }) {
    const query = { workchain, shard, seqno, offset, limit, sort: 'asc' };

    const { data: result } = await http.get('/transactions', { params: query });

    // Convert address hex notation to base64:
    result.transactions.forEach((tx) => {
        tx.account = result.address_book[tx.account].user_friendly; /* eslint no-param-reassign: "off" */
    });

    return result;
};

/**
 * @param  {Number} options.seqno
 * @return {Promise<Object>}
 */
export const getShards = async function ({ seqno }) {
    const { data: { blocks: result } } = await http.get('masterchainBlockShardState', { params: { seqno } });

    const shards = result.filter(block => block.workchain >= 0).reverse();

    return shards;
};

/**
 * @return {Promise<Object>}
 */
export const getLastBlock = async function () {
    const { data: result } = await http.get('masterchainInfo');

    return Object.freeze(result.last);
};

/**
 * This function extracts the data, that may be unique for every msg
 *
 * @param  {Object} msg
 * @return {Object}
 */
const parseMessageData = function extractMessageDetails(msg, addressBook) {
    const from = addressBook[msg.source]?.user_friendly || null;
    const to = addressBook[msg.destination]?.user_friendly || null;

    const message = msg?.message_content?.decoded?.type === 'text_comment' ? msg?.message_content?.decoded?.comment : undefined;

    return { from, to,
        message,
        amount: msg.value,
        op: msg.opcode ? `0x${(msg.opcode >>> 0).toString(16)}` : null, // eslint-disable-line no-bitwise
        is_bounced: msg.bounced === true,
    };
};

/**
 * @return {Promise<Object>}
 */
export const getTransactionsV3 = async function (address, { limit, offset, end_lt }) {
    // Chosen dates from calendar
    const start_utime = store.state.datePickerFilter[0];
    const end_utime = store.state.datePickerFilter[1];

    const { data: result } = await http.get('transactions', {
        params: {
            account: address,
            limit,
            offset,
            end_lt,
            start_utime,
            end_utime,
        },
    });

    const addressBook = result?.address_book;
    const transactions = result?.transactions;

    const groups = [];

    transactions.forEach((tx) => {
        const is_service = !tx.in_msg && tx.out_msgs.length === 0;

        const is_external = tx.out_msgs.length === 0
            && !tx.in_msg?.source
            && addressBook[tx.in_msg?.destination] === address;

        const newWalletTxSuccess = tx.description?.action?.result_code === undefined && tx.description?.compute_ph?.exit_code === undefined;
        const executionSuccess = tx.description.action?.action_result_code !== null && parseInt(tx.description?.action?.result_code, 10) <= 1;

        const is_success = newWalletTxSuccess || executionSuccess;
        const exit_code = is_success
            ? tx.description?.action?.result_code
            : tx.description?.compute_ph?.exit_code;

        const txDetails = {
            address,
            hash: tx.hash,
            fee: tx.total_fees,
            lt: tx.lt,
            timestamp: parseInt(tx.now + '000', 10),
            output_count: tx.out_msgs.length,
            exit_code: exit_code || 0,
            messages: [],
        };

        const msgDetails = {
            is_service, is_external, is_success,
            is_aggregated: false,
        };

        // Don't display long message list (e.g. multisends), show aggregated info instead:
        if (tx.out_msgs.length > 10) {
            const aggregatedAmount = tx.out_msgs.reduce((total, outMsg) => parseInt(outMsg.value, 10) + total, 0);
            txDetails.messages.push({ ...msgDetails,
                amount: aggregatedAmount,
                is_aggregated: true,
                is_bounced: tx.out_msgs.some(msg => msg.bounced),
                from: address,
                to: 'multiple destinations', // must be truthy to indicate that we do have the destination
            });

        // Otherwise push out_msgs to the list in chronological (reverse) order:
        } else {
            tx.out_msgs.reverse().forEach((outMsg) => {
                txDetails.messages.push({ ...msgDetails, ...parseMessageData(outMsg, addressBook) });
            });
        }

        // Then push the input message:
        if (tx.in_msg?.source) {
            txDetails.messages.push({ ...msgDetails, ...parseMessageData(tx.in_msg, addressBook) });
        }

        // Special case when there're neither out_msgs, nor in_msg (like in system contract):
        if (is_service) {
            txDetails.messages.push({ ...msgDetails,
                from: address,
                to: null,
            });
        }

        // Special case for external messages (e. g. when activating wallet):
        if (is_external) {
            txDetails.messages.push({ ...msgDetails,
                from: null,
                to: address,
            });
        }

        for (let i = 0; i < txDetails.messages.length; i += 1) {
            Object.freeze(txDetails.messages[i]);
        }

        groups.push(Object.freeze(txDetails));
    });

    return groups;
};

export const getCodeHash = async function (address) {
    let code_hash = undefined;

    const query = {
        account: address,
        sort: 'asc',
    };

    const { data: result } = await http.get('/transactions', { params: query });

    result?.transactions?.forEach((tx) => {
        if (tx?.account_state_after?.code_hash && !code_hash) {
            code_hash = tx.account_state_after.code_hash;
        }
    });

    return code_hash;
};

/**
 * The current account state with its code and data cells (base64 BOCs):
 * the contract tab reads them for the storage, the ABI and the Telegram
 * wallet chapter at once, so one request serves all of them.
 *
 * @param  {String} address
 * @return {Promise<Object|undefined>} { code_hash, code_boc, data_boc, status, … }
 */
export const getAccountStateBoc = dedupeInFlight(async (address) => {
    const { data: response } = await http.get('accountStates', {
        params: { address, include_boc: true },
    });

    return response?.accounts?.[0];
});

/**
 * What a Telegram wallet keeps in its data cell: revision, seqno, subwallet
 * id and the signing key. The layout is fixed for every revision (new ones
 * may only append fields), and the key is not in any action, so this is the
 * one place to read it from.
 *
 * @param  {String} address
 * @return {Promise<Object|null>} { revision, seqno, subwalletId, publicKey } or null
 */
export const getTgWalletState = async function (address) {
    const account = await getAccountStateBoc(address);

    if (account?.code_hash !== TG_WALLET_CODE_HASH || !account?.data_boc) {
        return null;
    }

    const { roots } = parseBocString(account.data_boc);
    const slice = roots[0].beginParse();

    return Object.freeze({
        revision: slice.loadUint(8),
        seqno: slice.loadUint(32),
        subwalletId: slice.loadUint(32),
        publicKey: bytesToHex(slice.loadBytes(32)),
    });
};

export const getWalletInfo = async function (address) {
    const { data: response } = await http.get(`wallet?address=${address}`);

    return response;
};

// Asked for by nearly every page, and often twice in a row — a redirect to
// the canonical spelling of an address remounts the page that just asked:
export const getAccountStates = dedupeInFlight(async (address) => {
    const { data: response } = await http.get(`accountStates?address=${address}&include_boc=false`);

    return response;
});

// Toncenter does not always name the sender of an NFT action: mints carry no
// source field at all, and a transfer of a freshly minted item comes without
// old_owner. The transactions know it anyway — the message that hit the NFT
// contract was sent by whoever minted or owned it.
const NFT_SENDER_ACTIONS = ['nft_mint', 'nft_transfer'];

const findNftSender = function (action, transactions) {
    const nftItem = action?.details?.nft_item;

    if (!nftItem || !transactions) {
        return undefined;
    }

    const itemTxHash = (action.transactions || []).find(hash => transactions[hash]?.account === nftItem);

    return transactions[itemTxHash]?.in_msg?.source;
};

/**
 * A page hands us whatever spelling of its own address the URL carried — the
 * bounceable EQ… form and the non-bounceable UQ… form name the very same
 * account. The address book is keyed to a single canonical spelling, and every
 * direction check below is a plain string comparison against it, so the
 * account is pinned to the book's spelling first.
 *
 * @param  {Object} addressBook
 * @param  {String} account
 * @return {String}
 */
const resolveAccountSpelling = function (addressBook, account) {
    if (!account || !addressBook) {
        return account;
    }

    const entries = Object.values(addressBook);

    // Already spelled the way the book spells it:
    if (entries.some(entry => entry?.user_friendly === account)) {
        return account;
    }

    const match = entries.find(entry => isSameAddress(entry?.user_friendly, account));

    return match?.user_friendly || account;
};

// Toncenter names the contract behind an address (address_book.interfaces,
// metadata.token_info), so a row can link it straight to its page instead of
// to /address, which would only look the same thing up and redirect. A pool
// carries the jetton_master interface as well, so pools are checked first.
// tv_pool is the nominator pool; a single nominator has no interface of its
// own (only a false daolama_vault), so it still goes through /address:
const CONTRACT_TYPE_BY_INTERFACE = Object.freeze({
    stonfi_pool: 'stonfi_pool',
    dedust_pool: 'dedust_pool',
    tonstake_pool: 'liquid_staking_pool',
    tv_pool: 'nominator_pool',
    jetton_master: 'jetton_master',
    jetton_wallet: 'jetton_wallet',
    jetton_wallet_v1: 'jetton_wallet',
    jetton_wallet_v2: 'jetton_wallet',
    jetton_wallet_governed: 'jetton_wallet',
    nft_collection: 'nft_collection',
    nft_item: 'nft_item',
    multisig_v2: 'multisig_wallet_v2',
});

export const getContractTypeByInterfaces = (interfaces) => {
    const name = Object.keys(CONTRACT_TYPE_BY_INTERFACE).find(key => (interfaces || []).includes(key));
    return name && CONTRACT_TYPE_BY_INTERFACE[name];
};

const CONTRACT_TYPE_BY_TOKEN_INFO = Object.freeze({
    jetton_masters: 'jetton_master',
    jetton_wallets: 'jetton_wallet',
    nft_collections: 'nft_collection',
    nft_items: 'nft_item',
});

// eslint-disable-next-line default-param-last
export const generateMessage = function (eventAction, addressBook, msgAccount, metadata = [], finality, traceTransactions) {
    msgAccount = resolveAccountSpelling(addressBook, msgAccount); // eslint-disable-line no-param-reassign

    let eventName = eventAction.type;
    const eventObj = eventAction.details;

    const messages = {
        from: undefined,
        to: undefined,
        action: undefined,
        event: eventName,
        meta: undefined,
        source_alias: undefined,
        destination_alias: undefined,
        message: eventObj?.encrypted ? null : (eventObj?.comment || null),
        encrypted: eventObj?.encrypted,
        is_external: eventName === 'Unknown',
        is_success: eventAction.success,
        is_swapped: eventName === 'jetton_swap',
        is_aggregated: false,
        is_bounced: false,
        is_service: false,
        op: eventObj?.opcode,
        finality: finality || eventObj?.finality || 'finalized',
        // Id of the gasless_request this action came out of, when Telegram's relayer paid for a Telegram
        // wallet (other relayed wallets, like W5, often pay the relayer in tokens, so they get no mark):
        gasless_parent: (isTgWallet(addressBook, eventObj?.source || eventObj?.sender)
            && eventAction.parent_gasless_action) || undefined,
    };

    // The first 32 bits of a message to a Telegram wallet are a slice of the
    // signature, not an opcode, so a deploy of such a wallet carries noise here:
    if (eventName === 'contract_deploy' && isTgWallet(addressBook, eventObj?.destination)) {
        messages.op = undefined;
    }

    function getAddressbook(address) {
        if (address && addressBook && addressBook[address]) {
            return addressBook[address].user_friendly;
        }
        return null;
    }

    function getAlias(address) {
        if (address && addressBook && addressBook[address]) {
            return addressBook[address].domain;
        }
        return undefined;
    }

    // Takes the user-friendly spelling that from/to are stored in:
    function getContractType(address) {
        const raw = address && Object.keys(addressBook || {}).find(key => addressBook[key]?.user_friendly === address);
        if (!raw) {
            return undefined;
        }

        const byInterface = getContractTypeByInterfaces(addressBook[raw].interfaces);
        if (byInterface) {
            return byInterface;
        }

        // Unindexed token_info is a bare guess (STON.fi v2 pools show up as jetton masters):
        const tokenInfo = metadata[raw]?.is_indexed ? metadata[raw].token_info?.[0] : undefined;
        return CONTRACT_TYPE_BY_TOKEN_INFO[tokenInfo?.type];
    }

    function getNftSenderFromTrace() {
        return getAddressbook(findNftSender(eventAction, traceTransactions));
    }

    function isFragmentTreasury(address) {
        if (!address) return false;
        return address.toUpperCase() === FRAGMENT_TREASURY_RAW
            || isSameAddress(address, FRAGMENT_TREASURY)
            || isSameAddress(getAddressbook(address), FRAGMENT_TREASURY);
    }

    // Teleitem sales come without old_owner. Fragment's own auctions pay the
    // treasury directly, so it is a real counterparty; any other payout target
    // is a one-shot escrow contract and must not be shown as a seller.
    function resolveNftSeller(obj) {
        return getAddressbook(obj?.old_owner)
            || getAddressbook(obj?.real_old_owner)
            || (isFragmentTreasury(obj?.payout_address)
                ? (getAddressbook(obj?.payout_address) || FRAGMENT_TREASURY)
                : null);
    }

    // Fragment never fills in the marketplace field, so its treasury taking the
    // payout or the royalty is the only hint. Never guess when the seller is
    // known: transaction lists print the marketplace instead of the sender and
    // would hide a real counterparty.
    function resolveNftMarketplace(obj) {
        if (obj?.marketplace) return obj.marketplace;
        if (obj?.old_owner || obj?.real_old_owner) return undefined;

        const paysFragment = isFragmentTreasury(obj?.payout_address)
            || isFragmentTreasury(obj?.marketplace_address)
            || isFragmentTreasury(obj?.royalty_address);

        return paysFragment ? 'fragment' : undefined;
    }

    // Exception - jettons sent to burn address are burned (wow)
    const isBurnAddress = eventObj.receiver === '0:0000000000000000000000000000000000000000000000000000000000000000'
        && eventName === 'jetton_transfer';

    if (isBurnAddress) {
        messages.event = 'jetton_burn';
        eventName = 'jetton_burn';
    }

    switch (eventName) {
        case 'ton_transfer': {
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;
            messages.event = msgAccount === getAddressbook(eventObj.source)
                ? 'sent_gram'
                : 'received_gram';
            break;
        }

        case 'subscribe': {
            messages.from = getAddressbook(eventObj.subscriber);
            messages.to = getAddressbook(eventObj.beneficiary);
            messages.source_alias = getAlias(eventObj.subscriber);
            messages.destination_alias = getAlias(eventObj.beneficiary);
            messages.amount = eventObj.amount;
            break;
        }

        case 'unsubscribe': {
            messages.from = getAddressbook(eventObj.subscriber);
            messages.to = getAddressbook(eventObj.beneficiary);
            messages.source_alias = getAlias(eventObj.subscriber);
            messages.destination_alias = getAlias(eventObj.beneficiary);
            break;
        }

        case 'call_contract': {
            messages.tonAmount = eventObj.value || 0;
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;
            break;
        }

        // A relayer delivered the owner's signed request and attached the gas
        // for it: source is the relayer, destination the wallet, value the gas
        // budget. A marker next to the actions it produced, not an operation.
        case 'gasless_request': {
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;
            break;
        }

        // The wallet rotated its signing key. The owner usually sends it as an
        // external message, so there is no source; a relayer for gasless ones.
        case 'change_wallet_key': {
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;
        }

        case 'nft_mint': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.meta = Object.freeze({
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
            });
            messages.from = getAddressbook(eventObj?.source) || getNftSenderFromTrace();
            messages.to = getAddressbook(eventObj.owner);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.event = 'deploy_nft';
            break;
        }

        case 'nft_transfer': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            const marketplace = resolveNftMarketplace(eventObj);
            messages.from = eventObj.is_purchase
                ? resolveNftSeller(eventObj)
                : getAddressbook(eventObj?.old_owner) || getNftSenderFromTrace();
            messages.to = getAddressbook(eventObj?.new_owner);
            // Nothing in the payload names the sender of a freshly minted item.
            // The item itself is what sent the notification, so it is the one
            // counterparty the row can honestly name.
            if (!messages.from && !eventObj.is_purchase) {
                messages.from = messages.action.nft;
            }
            messages.source_alias = getAlias(eventObj?.old_owner);
            messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace,
            };
            messages.event = 'nft_transfer';
            if (eventObj.is_purchase) {
                messages.event = 'nft_purchase';
                messages.amount = eventObj?.price;
                messages.meta.hideAmount = msgAccount !== messages.from && msgAccount !== getAddressbook(eventObj?.new_owner);
            }
            break;
        }

        case 'jetton_transfer': {
            // On a jetton wallet page the account is the sender's jetton wallet, not the sender.
            const isSender = msgAccount === getAddressbook(eventObj?.sender)
                || msgAccount === getAddressbook(eventObj?.sender_jetton_wallet);

            messages.from = getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.receiver);
            messages.source_alias = getAlias(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.receiver);
            messages.action = Object.freeze({
                type: isSender ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.amount,
                sender: getAddressbook(eventObj.sender),
                destination: messages.to = getAddressbook(eventObj.receiver),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                image: metadata[eventObj?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
            });
            messages.event = isSender
                ? 'sent_jetton'
                : 'received_jetton';
            break;
        }

        case 'jetton_mint': {
            messages.from = getAddressbook(eventObj.asset);
            messages.to = getAddressbook(eventObj.receiver);
            messages.source_alias = getAlias(eventObj.asset);
            messages.destination_alias = getAlias(eventObj.receiver);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
            });

            break;
        }

        case 'jetton_burn': {
            messages.from = getAddressbook(eventObj.owner) || getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.owner) || getAddressbook(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.amount = eventObj.amount;
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton: getAddressbook(eventObj.asset),
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                image: metadata[eventObj?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
            });
            messages.action = Object.freeze({
                type: 'jetton:burn',
                amount: eventObj.amount,
            });
            break;
        }

        case 'jetton_swap': {
            messages.amount_in = eventObj.dex_incoming_transfer.amount;
            messages.amount_out = eventObj.dex_outgoing_transfer.amount;
            messages.dex = eventObj?.dex;
            messages.action = Object.freeze({
                type: 'jetton:swap',
            });
            // STON.fi routes the native coin through its pTON jetton, whose own
            // metadata still carries the TON diamond, so that side is drawn as native too:
            const isNativeSide = (transfer) => {
                if (transfer?.asset === null) return true;
                const friendly = getAddressbook(transfer?.asset);
                return friendly !== null && isNativeTonAddress(friendly);
            };
            messages.meta = Object.freeze({
                jetton_in_address: getAddressbook(eventObj.dex_incoming_transfer?.asset),
                amount_in: eventObj.dex_incoming_transfer?.amount,
                symbol_in: metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.symbol,
                decimals_in: Number(metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.extra?.decimals),
                image_in: isNativeSide(eventObj.dex_incoming_transfer)
                    ? GRAM_LOGO_URL
                    : metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle

                jetton_out_address: getAddressbook(eventObj.dex_outgoing_transfer?.asset),
                amount_out: eventObj.dex_outgoing_transfer?.amount,
                symbol_out: metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.symbol,
                decimals_out: Number(metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.extra?.decimals),
                image_out: isNativeSide(eventObj.dex_outgoing_transfer)
                    ? GRAM_LOGO_URL
                    : metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle

                dex: eventObj?.dex,
            });
            messages.from = getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.dex_incoming_transfer.destination);
            messages.source_alias = getAlias(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.dex_incoming_transfer.destination);
            break;
        }

        case 'contract_deploy':
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;

            break;

        case 'renew_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'change_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'delete_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'auction_bid': {
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.from = getAddressbook(eventObj.bidder);
            messages.to = getAddressbook(eventObj?.auction || eventObj.nft_item);
            messages.source_alias = getAlias(eventObj.bidder);
            messages.destination_alias = getAlias(eventObj?.auction || eventObj.nft_item);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.amount = eventObj.amount;

            if (eventObj?.nft_item) {
                messages.action = Object.freeze({
                    type: 'nft:transfer_action',
                    nft: getAddressbook(eventObj.nft_item),
                });
            }

            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                symbol: 'GRAM',
            };

            break;
        }

        case 'election_deposit':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = 'Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF'; // Always elector
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias('Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF');
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'election_recover':
            messages.from = 'Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF'; // Always elector
            messages.to = getAddressbook(eventObj.stake_holder);
            messages.source_alias = getAlias('Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF');
            messages.destination_alias = getAlias(eventObj.stake_holder);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_withdrawal':
            messages.from = getAddressbook(eventObj.pool || eventObj.payout_nft);
            messages.to = getAddressbook(eventObj.stake_holder);
            messages.source_alias = getAlias(eventObj.pool || eventObj.payout_nft);
            messages.destination_alias = getAlias(eventObj.stake_holder);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_withdrawal_request':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias(eventObj.pool);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_deposit':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias(eventObj.pool);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'dex_withdraw_liquidity':
            // A withdrawal that is still one-sided comes without a pool, but
            // the liquidity contract it came from is always known:
            messages.from = getAddressbook(eventObj.pool || eventObj.destination_liquidity);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool || eventObj.destination_liquidity);
            messages.destination_alias = getAlias(eventObj.source);
            messages.dex = eventObj.dex;
            if (eventObj.pool) {
                messages.source_type = getLpPoolTypeByDex(eventObj.dex);
            }
            messages.details = Object.freeze({
                jetton_1_address: getAddressbook(eventObj.asset_1),
                amount_1: eventObj.amount_1,
                symbol_1: metadata[eventObj.asset_1]?.token_info[0]?.symbol,
                decimals_1: Number(metadata[eventObj.asset_1]?.token_info[0]?.extra?.decimals),
                jetton_2_address: getAddressbook(eventObj.asset_2),
                amount_2: eventObj.amount_2,
                symbol_2: metadata[eventObj.asset_2]?.token_info[0]?.symbol,
                decimals_2: Number(metadata[eventObj.asset_2]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'dex_deposit_liquidity':
            messages.from = getAddressbook(eventObj.source);
            // A deposit of a single asset is not paired with a pool yet, so the
            // liquidity contract it went to is the only destination there is:
            messages.to = getAddressbook(eventObj.pool || eventObj.destination_liquidity);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.pool || eventObj.destination_liquidity);
            messages.dex = eventObj.dex;
            if (eventObj.pool) {
                messages.destination_type = getLpPoolTypeByDex(eventObj.dex);
            }
            messages.details = Object.freeze({
                jetton_1_address: getAddressbook(eventObj.asset_1),
                amount_1: eventObj.amount_1,
                symbol_1: metadata[eventObj.asset_1]?.token_info[0]?.symbol,
                decimals_1: Number(metadata[eventObj.asset_1]?.token_info[0]?.extra?.decimals),
                jetton_2_address: getAddressbook(eventObj.asset_2),
                amount_2: eventObj.amount_2,
                symbol_2: metadata[eventObj.asset_2]?.token_info[0]?.symbol,
                decimals_2: Number(metadata[eventObj.asset_2]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'multisig_execute':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'multisig_create_order':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'multisig_approve':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'jvault_claim':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.action = Object.freeze({
                type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.claimed_rewards[0]?.amount,
                sender: getAddressbook(eventObj.pool),
                destination: messages.to = getAddressbook(eventObj.source),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.claimed_rewards[0]?.jetton]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.claimed_rewards[0]?.jetton),
                decimals: Number(metadata[eventObj.claimed_rewards[0]?.jetton]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'jvault_stake':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.pool);

            messages.action = Object.freeze({
                type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.amount,
                sender: getAddressbook(eventObj.source),
                destination: messages.to = getAddressbook(eventObj.pool),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'jvault_unstake_request':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.pool) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.pool),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'jvault_unstake':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.pool) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.pool),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'vesting_add_whitelist':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.vesting);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.vesting);
            break;

        case 'vesting_send_message':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.vesting);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.vesting);

            messages.amount = eventObj?.amount;
            break;

        case 'evaa_supply':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.master);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.master);

            messages.amount = eventObj.amount;

            messages.action = eventObj.is_ton
                ? {}
                : Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.source),
                    destination: messages.to = getAddressbook(eventObj.master),
                });
            messages.meta = eventObj.is_ton
                ? undefined
                : Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            break;

        case 'evaa_withdraw':
            messages.from = getAddressbook(eventObj.master);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.master);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj.amount;

            messages.action = eventObj.asset === null
                ? {}
                : Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.master),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
            messages.meta = eventObj.asset === null
                ? undefined
                : Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            break;

        case 'evaa_liquidate':
            messages.from = getAddressbook(eventObj.borrower);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.borrower);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.borrower) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.borrower),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'tick_tock':
            // console.log(eventObj);
            break;

        // Special NFT actions:
        case 'nft_put_on_sale': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.listing_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.listing_address);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.full_price;
            break;
        }

        case 'nft_cancel_sale': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.sale_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.sale_address);
            break;
        }

        case 'nft_put_on_auction': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.full_price;
            break;
        }

        case 'auction_outbid': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.auction_address);
            messages.to = getAddressbook(eventObj?.bidder);
            messages.source_alias = getAlias(eventObj?.auction_address);
            messages.destination_alias = getAlias(eventObj?.bidder);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.amount;
            break;
        }

        case 'teleitem_start_auction': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            messages.event = 'teleitem_start_auction';
            messages.amount = eventObj?.min_bid;
            break;
        }

        case 'teleitem_cancel_auction': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            break;
        }

        case 'nft_purchase': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            const marketplace = resolveNftMarketplace(eventObj);
            messages.from = resolveNftSeller(eventObj);
            messages.to = getAddressbook(eventObj?.new_owner);
            messages.source_alias = getAlias(eventObj?.old_owner);
            messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace,
            };
            messages.amount = eventObj?.price;
            messages.meta.hideAmount = msgAccount !== messages.from && msgAccount !== getAddressbook(eventObj?.new_owner);
            break;
        }

        case 'nft_update_sale': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj?.sale_contract);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj?.sale_contract);
            messages.amount = eventObj?.full_price;
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            break;
        }

        case 'dns_purchase': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.new_owner);
            // messages.to = getAddressbook(eventObj?.new_owner);
            messages.source_alias = getAlias(eventObj?.new_owner);
            // messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                collectionAddress: getAddressbook(eventObj.nft_collection),
                marketplace: eventObj?.marketplace,
            };
            messages.amount = eventObj?.price;
            messages.meta.hideAmount = msgAccount !== getAddressbook(eventObj?.old_owner) && msgAccount !== getAddressbook(eventObj?.new_owner);
            break;
        }

        case 'dns_release': {
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.amount = eventObj?.value;
            break;
        }

        // Toncenter could not tell what the trace did and fills in nothing but its
        // transactions: there is no counterparty or amount to show.
        case 'unknown': {
            break;
        }

        case 'nft_finish_auction':
        case 'nft_cancel_auction': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.meta = {
                marketplace: eventObj?.marketplace,
            };
            break;
        }

        default:
            // For test only:
            // In the case if we have an event that was not found previously - we can print it in alert

            // console.log('Found an unknown event: ' + eventName, eventObj);

            // Return empty object so tx table won't be broken
            return {};
    }

    // A contract type named by Toncenter beats the wallet assumed by the NFT
    // cases above (an auction bid goes to the NFT item itself). DEX pools keep
    // the type taken from the action: Toncenter leaves theirs empty.
    messages.source_type = getContractType(messages.from) || messages.source_type;
    messages.destination_type = getContractType(messages.to) || messages.destination_type;

    return messages;
};

/**
 * A liquidity action covers two assets at once and gets a row per asset. Only
 * one of them may be there, though: a deposit that is still waiting for its
 * pair comes with the second asset left empty, and an empty asset is not a row
 * showing nothing — it is no row at all.
 *
 * @param  {Object} msg         message built by generateMessage
 * @param  {String} typePrefix  action type the rows are tagged with
 * @return {Array}
 */
export const buildLiquidityMessages = function (msg, typePrefix = 'dex:deposit_liquidity_asset_') {
    return [1, 2].reduce((rows, asset) => {
        const amount = msg.details?.[`amount_${asset}`];

        if (amount === null || amount === undefined) {
            return rows;
        }

        rows.push({
            ...msg,
            action: {
                type: `${typePrefix}${asset}`,
            },
            meta: {
                symbol: msg.details[`symbol_${asset}`],
                jetton_address: msg.details[`jetton_${asset}_address`],
                decimals: msg.details[`decimals_${asset}`],
                amount,
            },
        });

        return rows;
    }, []);
};

/**
 * One page of the account's actions, grouped into traces. `nextPage` (null on the last page) is
 * what to pass back for the next one: toncenter's cursor and the actions of the page's last trace.
 * The limit may cut that trace, so it is held back and put in front of the next page: a trace is
 * one group of rows, and a gasless marker has to meet its twin.
 */
export const getToncenterActions = async function (account, { tx_hash, msg_hash, trace_id, start_lt, end_lt, limit, offset, cursor, heldBack }, filter) {
    // Chosen dates from calendar
    const start_utime = store.state.datePickerFilter[0];
    const end_utime = store.state.datePickerFilter[1];

    const params = {
        account,
        tx_hash,
        msg_hash,
        trace_id,
        start_utime,
        end_utime,
        limit,
        offset,
        start_lt,
        end_lt,
        cursor,
        sort: 'desc',
        // Without 'unknown' toncenter drops the transactions it could not classify, and an
        // account made only of those (a highload wallet sending empty batches) looks idle:
        supported_action_types: [...supported_action_types, 'unknown'],
        exclude_action_type: [],
    };

    // Excluding filter action groups
    const actionGroups = store.state.actionGroups;
    const excludeList = store.state.excludeGroups.flatMap(item => actionGroups[item]);

    params.exclude_action_type = excludeList;

    // Getting transactions direction
    let role = store.state.transactionDirection;
    if (role === 'all') role = undefined;
    params.role = role;

    // Only traces this account initiated (external message came to it)
    if (store.getters.initiatedByAccountFor(account)) {
        params.initiated_by_account = true;
    }

    // Adding filter for technical actions
    if (filter) {
        // params.exclude_action_type.push('contract_deploy');
        // params.exclude_action_type.push('call_contract');
    }

    const { data: response } = await http.get('/actions', {
        params,
        paramsSerializer: { indexes: null },
    });

    let pageActions = [...(heldBack?.actions || []), ...(response.actions || [])];
    const addressBook = { ...heldBack?.address_book, ...response.address_book };
    const metadata = { ...heldBack?.metadata, ...response.metadata };
    let nextPage = null;

    // The cursor only comes with a full page. Actions are sorted trace by trace, so the last trace is the tail:
    if (response.next_cursor) {
        const lastTraceId = pageActions.at(-1).trace_id;
        const lastTraceStart = pageActions.findIndex(action => action.trace_id === lastTraceId);

        nextPage = Object.freeze({
            cursor: response.next_cursor,
            heldBack: { actions: pageActions.slice(lastTraceStart), address_book: addressBook, metadata },
        });

        pageActions = pageActions.slice(0, lastTraceStart);
    }

    const actions = [];

    dropGaslessMarkers(dropGaslessTwins(pageActions), addressBook).forEach((action) => {
        const findByHash = actions.findIndex(({ hash }) => hash === action.trace_id);

        const msg = generateMessage(action, addressBook, account, metadata);

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_id,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: buildLiquidityMessages(msg),
                action: 'ok',
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_id,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    actions.nextPage = nextPage;

    return actions;

    // return response.actions.map(action => Object.freeze({
    //     address: account,
    //     fee: null,
    //     hash: action.trace_id,
    //     lt: action.end_lt,
    //     timestamp: action.end_utime * 1000,
    //     messages: [generateMessage(action, response.address_book, account, response.metadata)],
    //     action: 'ok',
    // }));
};

export const getPendingActions = async function (account) {
    const { data: response } = await http.get('/pendingActions', {
        params: {
            account,
        },
        headers: httpHeaders,
    });

    if (!response?.actions) return [];

    const actions = [];

    dropGaslessMarkers(dropGaslessTwins(dropExcludedActions(response.actions)), response.address_book).forEach((action) => {
        const findByHash = actions.findIndex(({ hash }) => hash === action.trace_external_hash_norm);

        // The endpoint returns every recent trace the index has not caught up with, and most of them
        // are already confirmed or finalized. Their notifications went out before the page subscribed,
        // so a row marked pending here would stay pending for good.
        const finality = action.finality || 'pending';

        const msg = generateMessage(action, response.address_book, account, response.metadata, finality);

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_external_hash_norm || action.trace_external_hash || action.trace_id,
                external_hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                timestamp: action.end_utime * 1000,
                messages: buildLiquidityMessages(msg),
                action: 'ok',
                finality,
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_external_hash_norm || action.trace_external_hash || action.trace_id,
                external_hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
                finality,
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    return actions;
};

/**
 * Finality of the traces that are already in the index, keyed by the normalized external message
 * hash the pending rows use. msg_hash finds a trace by its root message, and a hash the index
 * does not know is simply missing from the answer.
 * @param  {Array<String>} hashes
 * @return {Promise<Object>}
 */
export const getIndexedTraceFinality = async function (hashes) {
    const { data: response } = await http.get('/actions', {
        params: {
            msg_hash: hashes,
            supported_action_types,
            limit: 100,
        },
        paramsSerializer: { indexes: null },
    });

    const finality = {};

    (response?.actions || []).forEach((action) => {
        finality[action.trace_external_hash_norm || action.trace_external_hash] = action.finality || 'finalized';
    });

    return finality;
};

export const getNFTActions = async function (account, limit, offset) {
    const params = {
        account,
        limit,
        offset,
        include_accounts: false,
        sort: 'desc',
        action_type: [
            'nft_put_on_sale', 'nft_put_on_auction', 'nft_cancel_sale', 'nft_purchase',
            'nft_cancel_auction', 'nft_finish_auction', 'teleitem_start_auction', 'teleitem_cancel_auction',
            'nft_update_sale', 'dns_purchase', 'dns_release', 'nft_transfer', 'nft_mint',
        ],
        supported_action_types: [
            'nft.v3',
        ],
        exclude_action_type: [
            'auction_outbid',
        ],
    };

    const { data: response } = await http.get('/actions', {
        params,
        paramsSerializer: { indexes: null },
    });

    const actions = [];

    response.actions.forEach((action) => {
        const msg = generateMessage(action, response.address_book, account, response.metadata);

        actions.push({
            hash: action.trace_id,
            timestamp: action.end_utime * 1000,
            message: msg,
        });
    });

    return actions;
};

export const getNftByAddress = async function (address) {
    const { data: response } = await http.get('nft/items', {
        params: {
            address,
        },
    });

    return response;
};

export const getNFTsByOwnerAddress = async function (address, limit = 12, offset = 0) {
    const { data: response } = await http.get('nft/items', {
        params: {
            owner_address: address,
            sort_by_last_transaction_lt: true,
            limit,
            offset,
        },
    });

    return response;
};

export const getNFTsByCollectionAddress = async function (address, limit = 12, offset = 0) {
    const { data: response } = await http.get('nft/items', {
        params: {
            collection_address: address,
            sort_by_last_transaction_lt: true,
            limit,
            offset,
        },
    });

    return response;
};

export const getNFTCollectionByAddress = async function (address) {
    const { data: response } = await http.get('nft/collections', {
        params: {
            collection_address: address,
        },
    });

    return response;
};

/**
 * Loads a single collection item by its position. Without explicit sorting
 * Toncenter returns items ordered by index, so the offset is positional
 * (same semantics as the legacy extender API).
 *
 * @param  {String} collectionAddress
 * @param  {Number} index
 * @return {Promise<Object>}
 */
export const getNFTByCollectionIndex = async function (collectionAddress, index) {
    const { data: response } = await http.get('nft/items', {
        params: {
            collection_address: collectionAddress,
            limit: 1,
            offset: Math.max(index - 1, 0),
        },
    });

    return response;
};

/**
 * Non-zero jetton wallets of `owner` for the given jetton masters only.
 *
 * @param  {String} owner
 * @param  {Array<String>} jettons  jetton master addresses
 * @return {Promise<Array>}
 */
export const getJettonWalletsByMasters = async function (owner, jettons) {
    const { data } = await http.get('/jetton/wallets', {
        params: {
            owner_address: owner,
            jetton_address: jettons,
            exclude_zero_balance: true,
            limit: jettons.length,
        },
    });

    return data?.jetton_wallets || [];
};

export const getJettonsByOwnerAddress = async function (address, limit = 20, offset = 0, sortTopJettons = false, withPrices = false) {
    const order_list = [
        '0:B113A994B5024A16719F69139328EB759596C38A25F59028B146FECDC3621DFE', // USDT
        '0:AA0BA121449FEDA569E02B12FA755D24E834A7454AECF4649590B6DF742AAC8F', // STAKED
        '0:B8EF4F77A17E5785BD31BA4DA50ABD91852F2B8FEBEE97AD6EE16D941F939198', // GRAM
        '0:7C3B4249FA1A9E0C0A830B5386EB33D805FA55F90CF03DE77492971B20B5EC98', // GEM
        '0:cd0efe78bff4c4539b76eab17293296c74f42cbf99ec499687fefec94893ed32', // TONNEL
        '0:8554D9516FE3471C341FBF743DC6F0D704B2656B2F39E7FC5112CAB96435FA28', // MY
        '0:2F956143C461769579BAEF2E32CC2D7BC18283F40D20BB03E432CD603AC33FFC', // NOT
        '0:AE3E6D351E576276E439E7168117FD64696FD6014CB90C77B2F2CBAACD4FCC00', // MAJOR
        '0:AFC49CB8786F21C87045B19EDE78FC6B46C51048513F8E9A6D44060199C1BF0C', // DOGS
    ].reverse();

    const { data: response } = await http.get('/jetton/wallets', {
        params: {
            owner_address: address,
            jetton_address: (sortTopJettons && !IS_TESTNET) ? order_list : undefined, // Get list of top jettons
            exclude_zero_balance: true,
            // The whole top list is fetched at once: the header shows the first
            // few, the rest is scanned for liquid staking jettons (see
            // liquidStaking.js) without a second request.
            limit: (sortTopJettons && !IS_TESTNET) ? order_list.length : limit,
            offset,
        },
    });

    // Exception for testnet
    if (IS_TESTNET) return response;

    if (response?.jetton_wallets) {
        response.jetton_wallets.sort((b, a) => order_list.indexOf(a.jetton) - order_list.indexOf(b.jetton));
    }

    // USD prices live behind a second API. The account header shows only an
    // icon, a ticker and a balance, so waiting on that request would delay
    // the whole row for numbers nobody reads — it is asked for only when the
    // caller says it needs prices.
    let prices = [];

    if (withPrices) {
        const jettonAddressList = [];
        response?.jetton_wallets.forEach((wallet) => {
            jettonAddressList.push(wallet.jetton);
        });

        prices = await getJettonPricesDyor(jettonAddressList);
    }

    response?.jetton_wallets.forEach((wallet) => {
        const getPrice = prices.filter(p => p.address === wallet.jetton.toLowerCase())[0]?.price || 0;
        const balance = wallet.balance;
        const decimals = Number(response?.metadata[wallet.jetton]?.token_info[0]?.extra?.decimals);

        wallet.decimals = decimals;
        wallet.jetton_price_usd = getPrice;
        wallet.balance_price_usd = (getPrice * balance) / (10 ** decimals);
    });

    return response;
};

/**
 * Turns raw toncenter actions into the rows the overview table renders.
 * Liquidity actions cover two assets at once and get a row per asset.
 *
 * @param  {Array}  actions
 * @param  {Object} options.addressBook
 * @param  {Object} options.metadata
 * @param  {String} options.account       address or hash the page is opened for
 * @param  {Object} options.transactions  trace transactions, when known
 * @return {Array}
 */
const buildActionPreviews = function (actions = [], { addressBook, metadata, account, transactions } = {}) {
    const rows = [];

    dropGaslessTwins(actions).forEach((action) => {
        const msg = generateMessage(action, addressBook, account, metadata, undefined, transactions);

        if (msg.event !== 'dex_deposit_liquidity' && msg.event !== 'dex_withdraw_liquidity') {
            rows.push({ ...action, simple_preview: msg });
            return;
        }

        buildLiquidityMessages(msg, 'dex:liquidity_asset_').forEach((preview) => {
            rows.push({ ...action, simple_preview: preview });
        });
    });

    return rows;
};

/**
 * Actions of a single trace, page by page.
 *
 * A trace with thousands of transactions weighs tens of megabytes when asked
 * for as a whole, and two thirds of that is transaction bodies the overview
 * table never reads. A page of actions is a few dozen kilobytes.
 *
 * @param  {String} traceId
 * @param  {Number} options.limit
 * @param  {Number} options.offset
 * @param  {String} options.account
 * @return {Promise<Object>} rows to render plus the number of actions the
 *                           server returned — a full page means there is more
 */
export const getTraceActions = async function (traceId, { limit, offset = 0, account } = {}) {
    const { data } = await http.get('/actions', {
        params: {
            trace_id: traceId,
            limit,
            offset,
            sort: 'asc',
            supported_action_types,
        },
        paramsSerializer: { indexes: null },
    });

    return {
        actions: buildActionPreviews(data.actions, {
            addressBook: data.address_book,
            metadata: data.metadata,
            account,
        }),
        count: data.actions?.length || 0,
    };
};

// A page of actions is capped at 100, and an NFT action points at two
// transactions, so a page can never ask for more than this many.
const NFT_SENDER_TX_LIMIT = 256;

/**
 * Looks up the senders toncenter leaves out of NFT action payloads.
 *
 * Only the transactions the rows on screen actually point at are fetched — a
 * couple of hundred kilobytes, against the tens of megabytes a whole trace
 * costs. Meant to run after the rows are painted: they read correctly without
 * it, they just name one side of the route instead of two.
 *
 * @param  {Array}  rows     rows from buildActionPreviews
 * @return {Promise<Map>}    action_id -> { from, source_alias, source_type }
 */
export const getNftActionSenders = async function (rows = []) {
    const patches = new Map();

    const unresolved = rows.filter(row => NFT_SENDER_ACTIONS.includes(row.type)
        && (!row.simple_preview?.from || row.simple_preview.from === row.simple_preview.action?.nft)
        && row.details?.nft_item);

    const hashes = [...new Set(unresolved.flatMap(row => row.transactions || []))];

    if (!hashes.length || hashes.length > NFT_SENDER_TX_LIMIT) {
        return patches;
    }

    const { data } = await http.get('/transactions', {
        params: {
            hash: hashes,
            limit: hashes.length,
        },
        paramsSerializer: { indexes: null },
    });

    const transactions = {};
    (data.transactions || []).forEach((tx) => {
        transactions[tx.hash] = tx;
    });

    unresolved.forEach((row) => {
        const sender = findNftSender(row, transactions);

        if (sender) {
            patches.set(row.action_id, {
                from: data.address_book?.[sender]?.user_friendly || sender,
                source_alias: data.address_book?.[sender]?.domain,
                source_type: getContractTypeByInterfaces(data.address_book?.[sender]?.interfaces) || 'wallet',
            });
        }
    });

    return patches;
};

/**
 * Turns one toncenter trace (the /traces item shape: trace tree, transactions
 * keyed by hash, actions) into what the transaction page renders: the diagram
 * tree with its connections, the action previews and the lookups. The
 * emulator gets the very same shape back from the emulate API, so it renders
 * through this too.
 *
 * @param  {Object} traceItem
 * @param  {Object} options.addressBook
 * @param  {Object} options.metadata
 * @param  {String} options.account   address or hash the view is built for
 * @param  {Boolean} options.pending
 * @return {Object}
 */
export const buildTraceView = function (traceItem, { addressBook, metadata, account, pending = false } = {}) {
    const { trace: rootTrace, transactions } = traceItem;

    const warning = traceItem?.warning === 'trace is too large';

    function getUserFriendlyAddress(addr) {
        return addressBook?.[addr]?.user_friendly || addr;
    }

    let currentId = 1;
    const connections = [];

    function findTransactionByHash(tx_hash) {
        return transactions[tx_hash];
    }

    function processTransactionFields(transaction) {
        return {
            ...transaction,
            account: getUserFriendlyAddress(transaction?.account),
            in_msg: {
                ...transaction.in_msg,
                source: transaction.in_msg?.source ? getUserFriendlyAddress(transaction.in_msg.source) : null,
                destination: transaction.in_msg?.destination ? getUserFriendlyAddress(transaction.in_msg.destination) : null,
            },
            out_msgs: transaction.out_msgs?.map(msg => ({
                ...msg,
                source: msg.source ? getUserFriendlyAddress(msg.source) : null,
                destination: msg.destination ? getUserFriendlyAddress(msg.destination) : null,
            })) || [],
        };
    }

    function countAllDescendants(node) {
        if (!node.children || node.children.length === 0) {
            return 0;
        }
        return node.children.reduce((sum, child) => sum + countAllDescendants(child) + 1, 0);
    }

    function sortChildrenCentered(children) {
        if (children.length <= 1) return children;

        const sorted = [...children].sort((a, b) => a.childrenCount - b.childrenCount);

        const middleIndex = Math.floor((sorted.length - 1) / 2);
        const middleElement = sorted.shift();
        const finalSorted = [...sorted.slice(0, middleIndex), middleElement, ...sorted.slice(middleIndex)];

        return finalSorted;
    }

    function processNode(node, parentName = null) {
        let transaction = findTransactionByHash(node.tx_hash);
        transaction = processTransactionFields(transaction);

        const canonize = getUserFriendlyAddress(transaction.account);
        const name = `${canonize.slice(0, 2)}...${canonize.slice(-4)}`;
        const id = currentId;
        currentId += 1;
        const children = node.children ? Object.values(node.children).map(child => processNode(child, name)) : [];

        if (parentName) {
            connections.push({
                source: parentName,
                target: name,
                label: 'Oops',
                value: transaction.in_msg.value,
            });
        }

        const childrenWithCount = children.map(child => ({
            ...child,
            childrenCount: countAllDescendants(child),
        }));

        const sortedChildren = sortChildrenCentered(childrenWithCount);

        return {
            id,
            name,
            status: transaction.in_msg?.decoded_opcode === 'jetton_notify' || (transaction.description?.compute_ph?.success && !transaction.description?.aborted),
            transaction,
            interfaces: 'jetton',
            address: getUserFriendlyAddress(transaction.account),
            label: transaction.in_msg?.decoded_opcode || transaction.in_msg?.opcode,
            value: transaction.in_msg.value,
            opCode: transaction.in_msg.opcode,
            children: sortedChildren,
        };
    }

    const trace = warning ? {} : processNode(rootTrace);

    const series = {
        traceId: traceItem.trace_id,
        timeStart: traceItem.start_utime,
    };

    const actions = [];

    if (warning) {
        traceItem.actions = actions; // eslint-disable-line no-param-reassign
        return {
            events: traceItem,
            trace,
            connections,
            series,
            duration: traceItem.end_utime - traceItem.start_utime,
            pending,
            warning,
        };
    }

    actions.push(...buildActionPreviews(traceItem.actions, {
        addressBook,
        metadata,
        account,
        transactions,
    }));

    traceItem.actions = actions; // eslint-disable-line no-param-reassign

    return {
        events: traceItem,
        trace,
        connections,
        series,
        duration: traceItem.end_utime - traceItem.start_utime,
        pending,
        // Raw-to-friendly addresses and jetton metadata of everything in the
        // trace, for views that read the transactions and action details
        // themselves (the value flow table):
        addressBook: addressBook || {},
        metadata: metadata || {},
    };
};

export const getTransactionTraceV3 = async function getEventsByAccount(hash, msge, signal) {
    const queryParam = msge ? `msg_hash=${hash}` : `tx_hash=${hash}`;
    let response = await http.get(`traces?${queryParam}`, {
        signal,
        params: {
            include_actions: true,
            supported_action_types,
        },
        paramsSerializer: { indexes: null },
    });

    if (!response?.data?.traces?.length && !msge) {
        response = await http.get(`traces?msg_hash=${hash}`, {
            signal,
            params: {
                include_actions: true,
                supported_action_types,
            },
        });
    }

    let pending = false;

    // Means this is pending transaction
    if (!response?.data?.traces?.[0]) {
        pending = true;

        response = await http.get('/pendingTraces', {
            signal,
            params: {
                ext_msg_hash: hash,
            },
        });
    }

    // If still no traces found, throw an error
    if (!response?.data?.traces?.[0]) {
        throw new Error('Transaction trace not found');
    }

    return buildTraceView(response.data.traces[0], {
        addressBook: response.data.address_book,
        metadata: response.data.metadata,
        account: hash,
        pending,
    });
};

export const getJettonInfo = dedupeInFlight(async (address) => {
    try {
        const response = await http.get('jetton/masters', { params: { address } });
        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
});

const toFriendlyAddress = function (address) {
    try {
        return canonizeAddress(address);
    } catch (error) {
        return '';
    }
};

/**
 * jetton/masters accepts several addresses per call, so a page needing
 * metadata for more than one master gets it in a single round trip. The
 * response is split back into per-address chunks shaped like
 * getJettonInfo's, so callers keep the same parsing.
 *
 * @param  {Array<String>} addresses
 * @return {Promise<Object>} keyed by the requested address
 */
export const getJettonInfoBatch = async function (addresses = []) {
    const requested = [...new Set(addresses.filter(Boolean))];

    if (!requested.length) {
        return {};
    }

    const { data } = await http.get('jetton/masters', {
        params: {
            address: requested,
            limit: requested.length,
        },
        paramsSerializer: { indexes: null },
    });

    const masters = data?.jetton_masters || [];
    const addressBook = data?.address_book || {};
    const result = {};

    requested.forEach((address) => {
        const friendly = toFriendlyAddress(address);
        const master = masters.find(item => isSameAddress(
            toFriendlyAddress(addressBook[item.address]?.user_friendly || item.address),
            friendly,
        ));

        result[address] = {
            jetton_masters: master ? [master] : [],
            metadata: data?.metadata || {},
            address_book: addressBook,
        };
    });

    return result;
};

/**
 * When the contract first showed up on chain, taken from its oldest
 * transaction — toncenter exposes no dedicated field for it.
 *
 * @param  {String} address
 * @return {Promise<Number|null>} unix seconds
 */
export const getAccountCreationTime = async function (address) {
    const { data } = await http.get('/transactions', {
        params: {
            account: address,
            sort: 'asc',
            limit: 1,
        },
    });

    return data?.transactions?.[0]?.now ?? null;
};

// Resolves an off-chain jetton content URI (ipfs:// or https://) into the
// metadata JSON. Used as a fallback when toncenter has not yet indexed the
// off-chain payload (token_info.valid === false / empty name/symbol/image).
export const fetchOffchainJettonMetadata = async (uri) => {
    if (!uri || typeof uri !== 'string') return null;

    const url = uri.startsWith('ipfs://')
        ? uri.replace('ipfs://', 'https://ipfs.io/ipfs/')
        : uri;

    const { data } = await axios.get(url, {
        timeout: 10000,
        responseType: 'json',
    });

    if (!data || typeof data !== 'object') return null;
    return data;
};

export const getJettonHolders = async function (jetton_address, offset = 0, limit = 12) {
    const { data } = await http.get('jetton/wallets', { params: {
        jetton_address,
        offset,
        limit,
        sort: 'desc',
        exclude_zero_balance: true,
    } });

    return data;
};

export const getJettonWalletInfo = async function (address) {
    const { data } = await http.get('jetton/wallets', { params: {
        address,
    } });

    return data;
};

/**
 * @param  {String} ownerAddress
 * @param  {String} jettonAddress  the jetton master
 * @return {Promise<String>} nanojettons, '0' when the owner has no wallet
 */
export const getJettonBalance = async function (ownerAddress, jettonAddress) {
    const { data } = await http.get('jetton/wallets', { params: {
        owner_address: ownerAddress,
        jetton_address: jettonAddress,
        limit: 1,
    } });

    return data.jetton_wallets?.[0]?.balance ?? '0';
};

export const getMultisig = async function (address) {
    const { data } = await http.get('multisig/wallets', { params: {
        address,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

export const getMultisigOrder = async function (address) {
    const { data } = await http.get('multisig/orders', { params: {
        address,
        parse_actions: true,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

export const getVesting = async function (address) {
    const { data } = await http.get('vesting', { params: {
        contract_address: address,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

/**
 * Resolve raw/user-friendly address via toncenter address_book.
 * @param  {Object} addressBook
 * @param  {String} address
 * @return {String}
 */
const resolvePoolAddress = function (addressBook, address) {
    if (!address) {
        return address;
    }

    return addressBook?.[address]?.user_friendly
        || canonizeAddress(address);
};

/**
 * Map toncenter v3 nominator pool response to the legacy shape
 * expected by Pool.vue (fields that exist in the new API only).
 *
 * @param  {Object} data
 * @return {Object}
 */
const mapNominatorPoolInfo = function (data) {
    const addressBook = data.address_book || {};
    const activeNominators = data.active_nominators || [];
    const totalNominatorStake = activeNominators.reduce(
        (sum, nominator) => sum + parseInt(nominator.balance || 0, 10),
        0,
    );

    return {
        state: data.state,
        nominator_count: data.nominators_count,
        stake_amount_sent: data.stake_amount_sent,
        validator_amount: data.validator_amount,
        validator_address: resolvePoolAddress(addressBook, data.validator_address),
        // Toncenter returns basis points (4000 => 0.4):
        validator_reward_share: Number(data.validator_reward_share) / 10000,
        max_nominators_count: data.max_nominators_count,
        min_validator_stake: data.min_validator_stake,
        min_nominator_stake: data.min_nominator_stake,
        current_nominator_stake: String(totalNominatorStake),
    };
};

/**
 * Nominator pool info from Toncenter v3, adapted to the legacy response shape.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getNominatorPoolInfo = async function (address) {
    const { data } = await http.get('staking/nominatorPools/pool', {
        params: { pool: address },
    });

    return mapNominatorPoolInfo(data);
};

/**
 * Nominator list from Toncenter v3, adapted to the legacy response shape.
 * Unlike the legacy extender API, Toncenter doesn't expose withdrawal requests,
 * so the returned rows have no withdraw_requested field.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getNominatorPoolNominators = async function (address) {
    const { data } = await http.get('staking/nominatorPools/pool', {
        params: { pool: address },
    });

    const addressBook = data.address_book || {};
    const activeNominators = data.active_nominators || [];
    const totalStake = activeNominators.reduce(
        (sum, nominator) => sum + parseInt(nominator.balance || 0, 10),
        0,
    );

    const addresses = {};
    const nominators = activeNominators.map((nominator) => {
        const friendly = addressBook[nominator.address]?.user_friendly || nominator.address;
        addresses[friendly] = { alias: addressBook[nominator.address]?.domain || null };

        return {
            address: friendly,
            amount: nominator.balance,
            pending_deposit_amount: nominator.pending_balance,
            weight: totalStake > 0 ? parseInt(nominator.balance || 0, 10) / totalStake : 0,
        };
    }).sort((a, b) => b.weight - a.weight);

    return { nominators, '@addresses': addresses };
};

/**
 * Executes a contract get-method on the current account state.
 * Toncenter answers with HTTP 200 even when the method fails, so the
 * TVM exit code has to be checked by the caller.
 *
 * @param  {String} address
 * @param  {String} method
 * @param  {Array} stack
 * @return {Promise<Object>} { exit_code, gas_used, stack }
 */
export const runGetMethod = async function (address, method, stack = []) {
    const { data } = await http.post('runGetMethod', { address, method, stack });

    return data;
};

// SHARE_BASIS in the liquid staking pool contract: interest rate and
// governance fee are stored as a fraction of 2^24.
const LIQUID_POOL_SHARE_BASIS = 2 ** 24;

// validators_elected_for on mainnet, used to annualize one round of pool
// income. Note that a loan is only settled several rotations after it is
// issued, so the resulting APY runs well above what pools advertise —
// raise this value if the number has to match their numbers.
const LIQUID_POOL_ROUND_SECONDS = 65536;

const SECONDS_PER_YEAR = 365 * 24 * 60 * 60;

/**
 * Reads a numeric entry of a get-method stack. Values arrive as hex
 * strings and may be negative ('-0x1' is the TVM's true).
 *
 * @param  {Object} entry
 * @return {Number|null}
 */
export const readStackNumber = function (entry) {
    if (entry?.type !== 'num') {
        return null;
    }

    const value = parseInt(entry.value, 16);
    return Number.isNaN(value) ? null : value;
};

export const readStackBoolean = function (entry) {
    const value = readStackNumber(entry);
    return value === null ? null : value !== 0;
};

/**
 * NFT collection royalty as a percent, via TEP-66 royalty_params.
 * Collections without the method (non-zero exit code) yield null so
 * the UI can hide the royalty block instead of keeping the skeleton.
 *
 * @param  {String} address
 * @return {Promise<Number|null>}
 */
export const getNftRoyaltyPercent = async function (address) {
    const { exit_code, stack } = await runGetMethod(address, 'royalty_params');

    if (exit_code !== 0 || !stack?.length) {
        return null;
    }

    const numerator = readStackNumber(stack[0]);
    const denominator = readStackNumber(stack[1]);

    if (numerator == null || !denominator) {
        return null;
    }

    const percent = (numerator / denominator) * 100;

    if (!percent) {
        return null;
    }

    return Number.isInteger(percent) ? percent : Math.round(percent * 100) / 100;
};

/**
 * Reads an address entry. addr_none is returned as an empty list rather
 * than a cell, so a missing address yields null.
 *
 * @param  {Object} entry
 * @return {String|null}
 */
export const readStackAddress = function (entry) {
    return entry?.type === 'cell' ? parseAddressCell(entry.value) : null;
};

/**
 * Reads a round tuple:
 * [borrowers_dict, round_id, active_borrowers, borrowed, expected, returned, profit]
 * The borrowers dictionary itself is left alone — decoding it requires a
 * full hashmap parser, and the aggregates below are enough for the UI.
 *
 * @param  {Object} entry
 * @return {Object}
 */
const readStackRound = function (entry) {
    const items = entry?.type === 'tuple' ? entry.value : [];

    return {
        round_id: readStackNumber(items[1]),
        active_borrowers: readStackNumber(items[2]),
        borrowed: readStackNumber(items[3]),
        expected: readStackNumber(items[4]),
        returned: readStackNumber(items[5]),
        profit: readStackNumber(items[6]),
    };
};

/**
 * Liquid staking pool data, read straight from the contract via
 * get_pool_full_data. Covers both Tonstakers and Stakee style pools —
 * they share the ton-blockchain/liquid-staking-contract code.
 *
 * The projected conversion rate is what the rate becomes once the
 * previous round is settled, so the growth between the current and the
 * projected rate is one round worth of income.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getLiquidStakingPoolInfo = async function (address) {
    const { exit_code, stack } = await runGetMethod(address, 'get_pool_full_data');

    // 30 values, see compose_pool_full_data_internal() in pool.func:
    if (exit_code !== 0 || stack?.length !== 30) {
        throw new Error(`get_pool_full_data failed on ${address}: exit code ${exit_code}`);
    }

    const totalBalance = readStackNumber(stack[2]);
    const supply = readStackNumber(stack[13]);
    const projectedBalance = readStackNumber(stack[28]);
    const projectedSupply = readStackNumber(stack[29]);

    const rate = supply > 0 ? totalBalance / supply : null;
    const projectedRate = projectedSupply > 0 ? projectedBalance / projectedSupply : null;

    let apy = null;

    if (rate > 0 && projectedRate > 0) {
        const roundGrowth = (projectedRate / rate) - 1;
        const roundsPerYear = SECONDS_PER_YEAR / LIQUID_POOL_ROUND_SECONDS;
        apy = ((((1 + roundGrowth) ** roundsPerYear) - 1) * 100);
    }

    return {
        state: readStackNumber(stack[0]),
        halted: readStackBoolean(stack[1]),
        total_balance: totalBalance,
        interest_rate: readStackNumber(stack[3]) / LIQUID_POOL_SHARE_BASIS,
        optimistic_deposit_withdrawals: readStackBoolean(stack[4]),
        deposits_open: readStackBoolean(stack[5]),
        prev_round: readStackRound(stack[7]),
        current_round: readStackRound(stack[8]),
        min_loan_per_validator: readStackNumber(stack[9]),
        max_loan_per_validator: readStackNumber(stack[10]),
        governance_fee: readStackNumber(stack[11]) / LIQUID_POOL_SHARE_BASIS,
        jetton_minter: readStackAddress(stack[12]),
        supply,
        requested_for_deposit: readStackNumber(stack[15]),
        requested_for_withdrawal: readStackNumber(stack[17]),
        governor: readStackAddress(stack[20]),
        interest_manager: readStackAddress(stack[22]),
        halter: readStackAddress(stack[23]),
        approver: readStackAddress(stack[24]),
        rate,
        projected_rate: projectedRate,
        apy,
    };
};

/**
 * Single nominator pool owner and validator, read via get_roles().
 * Replaces the legacy extender endpoint, which is no longer reliable.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getSingleNominatorData = async function (address) {
    const { exit_code, stack } = await runGetMethod(address, 'get_roles');

    if (exit_code !== 0 || stack?.length !== 2) {
        const error = new Error(`get_roles failed on ${address}: exit code ${exit_code}`);
        error.isInvalidContract = true; /* eslint no-param-reassign: "off" */
        throw error;
    }

    const ownerAddress = readStackAddress(stack[0]);
    const validatorAddress = readStackAddress(stack[1]);

    if (!ownerAddress || !validatorAddress) {
        const error = new Error(`get_roles returned unparseable addresses on ${address}`);
        error.isInvalidContract = true; /* eslint no-param-reassign: "off" */
        throw error;
    }

    return {
        owner_address: ownerAddress,
        validator_address: validatorAddress,
    };
};

const STONFI_FEE_DENOMINATOR = 10000;

const readStackCellBoc = function (entry) {
    return entry?.type === 'cell' ? entry.value : null;
};

const cloneTonAsset = function () {
    return { ...TON_NATIVE_ASSET };
};

/**
 * STON.fi stores the pool's jetton wallets, not the masters. Wallet
 * get-methods give us the master; the zero address / pTON is native TON.
 *
 * @param  {String|null} address
 * @return {Promise<Object>}
 */
const resolveStonfiAsset = async function (address) {
    if (isNativeTonAddress(address)) {
        return cloneTonAsset();
    }

    try {
        const { exit_code, stack } = await runGetMethod(address, 'get_wallet_data');

        if (exit_code === 0 && stack?.length >= 3) {
            const master = readStackAddress(stack[2]);

            if (isNativeTonAddress(master)) {
                return cloneTonAsset();
            }

            if (master) {
                return { type: 'jetton', address: master };
            }
        }
    } catch (error) {
        console.error('STON.fi wallet lookup failed:', error);
    }

    return { type: 'jetton', address };
};

/**
 * DeDust pool reserves, assets, fee and stable flag via get-methods.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getDedustPoolInfo = async function (address) {
    const [assetsResult, reservesResult, stableResult, feeResult] = await Promise.all([
        runGetMethod(address, 'get_assets'),
        runGetMethod(address, 'get_reserves'),
        runGetMethod(address, 'is_stable'),
        runGetMethod(address, 'get_trade_fee').catch(() => ({ exit_code: -1, stack: [] })),
    ]);

    if (assetsResult.exit_code !== 0 || assetsResult.stack?.length !== 2) {
        throw new Error(`get_assets failed on ${address}: exit code ${assetsResult.exit_code}`);
    }

    if (reservesResult.exit_code !== 0 || reservesResult.stack?.length !== 2) {
        throw new Error(`get_reserves failed on ${address}: exit code ${reservesResult.exit_code}`);
    }

    const asset0 = parseDedustAsset(readStackCellBoc(assetsResult.stack[0])) || cloneTonAsset();
    const asset1 = parseDedustAsset(readStackCellBoc(assetsResult.stack[1])) || cloneTonAsset();

    if (asset0.type === 'native') {
        Object.assign(asset0, cloneTonAsset());
    }

    if (asset1.type === 'native') {
        Object.assign(asset1, cloneTonAsset());
    }

    let fee = null;
    const feeNumerator = readStackNumber(feeResult.stack?.[0]);
    const feeDenominator = readStackNumber(feeResult.stack?.[1]);

    if (feeNumerator != null && feeDenominator) {
        fee = (feeNumerator / feeDenominator) * 100;
    }

    return {
        dex: 'dedust',
        is_stable: readStackBoolean(stableResult.stack?.[0]),
        fee,
        reserve0: readStackNumber(reservesResult.stack[0]),
        reserve1: readStackNumber(reservesResult.stack[1]),
        asset0,
        asset1,
    };
};

/**
 * STON.fi v1 returns 10 stack items; v2 and weighted stableswap return 12+.
 * Token slices are the pool's jetton wallets and have to be resolved.
 *
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getStonfiPoolInfo = async function (address) {
    const { exit_code, stack } = await runGetMethod(address, 'get_pool_data');

    if (exit_code !== 0 || !stack?.length) {
        throw new Error(`get_pool_data failed on ${address}: exit code ${exit_code}`);
    }

    let reserve0;
    let reserve1;
    let token0;
    let token1;
    let lpFee;
    let protocolFee;

    if (stack.length === 10) {
        reserve0 = readStackNumber(stack[0]);
        reserve1 = readStackNumber(stack[1]);
        token0 = readStackAddress(stack[2]);
        token1 = readStackAddress(stack[3]);
        lpFee = readStackNumber(stack[4]);
        protocolFee = readStackNumber(stack[5]);
    } else if (stack.length >= 12) {
        reserve0 = readStackNumber(stack[3]);
        reserve1 = readStackNumber(stack[4]);
        token0 = readStackAddress(stack[5]);
        token1 = readStackAddress(stack[6]);
        lpFee = readStackNumber(stack[7]);
        protocolFee = readStackNumber(stack[8]);
    } else {
        throw new Error(`get_pool_data returned ${stack.length} items on ${address}`);
    }

    const [asset0, asset1] = await Promise.all([
        resolveStonfiAsset(token0),
        resolveStonfiAsset(token1),
    ]);

    let fee = null;

    if (lpFee != null) {
        fee = ((lpFee + (protocolFee || 0)) / STONFI_FEE_DENOMINATOR) * 100;
    }

    return {
        dex: 'stonfi',
        is_stable: null,
        fee,
        reserve0,
        reserve1,
        asset0,
        asset1,
    };
};

/**
 * @param  {String} address
 * @param  {String} poolType  contractHashes value
 * @return {Promise<Object>}
 */
export const getDexPoolInfo = async function (address, poolType) {
    if (poolType === 'dedust_pool') {
        return getDedustPoolInfo(address);
    }

    if (poolType === 'stonfi_pool' || poolType === 'stonfi_pool_v2') {
        return getStonfiPoolInfo(address);
    }

    throw new Error(`Unsupported LP pool type ${poolType}`);
};
